import { useState } from 'react'
import { toast } from 'sonner'
import { Panel } from '@/components/redesign/bits'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { errorMessage } from '@/lib/api/errors'
import { formatRelative } from '@/lib/format'
import { type KnownFile, type LearnedThisWeek, useDecideKnownFile, useSecurityFeed } from './api'

const verdictLabels = { review: 'To review', malware: 'Malware', safe: 'Safe' } as const

const sourceLabels = {
  clamav: 'ClamAV, in a deep scan',
  rules: 'Arva SEO’s rules, in deep scans',
  patterns: 'The plugin’s patterns',
  staff: 'Added by staff',
  expected: 'Marked as expected on several websites',
} as const

/**
 * What the WordPress plugin's security scan learns between releases (SecurityFeed in the API):
 * malware ClamAV found on one website is known to all, files flagged on several websites wait
 * here, and staff can add fingerprints themselves.
 */
export function SecurityFeedCard() {
  const feed = useSecurityFeed()
  const decide = useDecideKnownFile()
  const [sha, setSha] = useState('')
  const [name, setName] = useState('')

  const mark = (file: Pick<KnownFile, 'sha256'>, verdict: 'malware' | 'safe', label?: string) =>
    decide.mutate(
      { sha256: file.sha256, verdict, ...(label ? { name: label } : {}) },
      {
        onSuccess: () =>
          toast.success(
            verdict === 'malware'
              ? 'Marked as malware for every website.'
              : 'Marked as safe for every website.',
          ),
        onError: (error) => toast.error(errorMessage(error)),
      },
    )

  return (
    <Panel
      title="Security feed"
      sub={
        feed.data
          ? `What the plugin’s security scan learns between releases, downloaded by every connected website once a day (feed ${feed.data.meta.version}). Malware ClamAV finds in one website’s deep scan is known to all. A file flagged on ${feed.data.meta.review_sites} or more websites waits here: mark it as malware, or as safe so the plugin stops reporting it. Only fingerprints are kept, never the files or which websites. PHP versions and new patterns come from resources/security/feed.json (${feed.data.meta.curated_version}, ${feed.data.meta.patterns} patterns).`
          : 'What the plugin’s security scan learns between releases.'
      }
    >
      {feed.isPending ? (
        <Skeleton className="h-24 rounded-md" />
      ) : feed.isError ? (
        <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />
      ) : (
        <>
          <LearnedThisWeekLine week={feed.data.meta.week} />
          <p className="m" style={{ margin: '0 0 12px' }}>
            {feed.data.meta.counts.review.toLocaleString()} to review ·{' '}
            {feed.data.meta.counts.malware.toLocaleString()} malware ·{' '}
            {feed.data.meta.counts.safe.toLocaleString()} safe
            {feed.data.meta.php && <> · PHP fixes from {Object.values(feed.data.meta.php).join(', ')}</>}
          </p>
          {feed.data.data.length === 0 ? (
            <p className="empty-line">
              Nothing learned yet: no deep scan has found malware, and no file was flagged on several
              websites.
            </p>
          ) : (
            <div className="tw" style={{ border: 0 }}>
              <table className="t" style={{ minWidth: 760 }}>
                <caption className="sr-only">Security feed</caption>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Found by</th>
                    <th className="r">Websites</th>
                    <th>Last seen</th>
                    <th>Verdict</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {feed.data.data.map((f) => (
                    <tr key={f.sha256}>
                      <td style={{ wordBreak: 'break-all' }}>
                        <b>{f.name ?? 'Unnamed'}</b>
                        <div className="m num" title={f.sha256}>
                          {f.sha256.slice(0, 16)}…
                        </div>
                        {f.excerpt && (
                          <code
                            className="m"
                            style={{ display: 'block', marginTop: 4, wordBreak: 'break-all' }}
                          >
                            {f.excerpt}
                          </code>
                        )}
                      </td>
                      <td className="m">{sourceLabels[f.source]}</td>
                      <td className="r num">{f.sites.toLocaleString()}</td>
                      <td className="m" style={{ whiteSpace: 'nowrap' }}>
                        {formatRelative(f.last_seen_at)}
                      </td>
                      <td>
                        <b>{verdictLabels[f.verdict]}</b>
                        {f.decided_by && <div className="m">by {f.decided_by}</div>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {f.verdict !== 'malware' && (
                          <button
                            type="button"
                            className="btn sm"
                            disabled={decide.isPending}
                            onClick={() => mark(f, 'malware')}
                          >
                            Malware
                          </button>
                        )}{' '}
                        {f.verdict !== 'safe' && (
                          <button
                            type="button"
                            className="btn sm ghost"
                            disabled={decide.isPending}
                            onClick={() => mark(f, 'safe')}
                          >
                            Safe
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <form
            style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}
            onSubmit={(e) => {
              e.preventDefault()
              decide.mutate(
                {
                  sha256: sha.trim().toLowerCase(),
                  verdict: 'malware',
                  ...(name.trim() ? { name: name.trim() } : {}),
                },
                {
                  onSuccess: () => {
                    toast.success('Added: every website’s next scan reports this file as malware.')
                    setSha('')
                    setName('')
                  },
                  onError: (error) => toast.error(errorMessage(error)),
                },
              )
            }}
          >
            <Field
              label="Add malware by its SHA-256"
              htmlFor="feed-sha"
              required
              className="min-w-[280px] flex-1"
            >
              <Input
                id="feed-sha"
                value={sha}
                onChange={(e) => setSha(e.target.value)}
                placeholder="64 letters and digits"
                required
              />
            </Field>
            <Field label="What it is" htmlFor="feed-name" className="min-w-[200px]">
              <Input
                id="feed-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="A webshell from…"
              />
            </Field>
            <button
              type="submit"
              className="btn"
              disabled={decide.isPending || !/^[A-Fa-f0-9]{64}$/.test(sha.trim())}
            >
              Add
            </button>
          </form>
        </>
      )}
    </Panel>
  )
}

/** What the scanner learned in the last 7 days, in one line. */
function LearnedThisWeekLine({ week }: { week: LearnedThisWeek }) {
  const parts = [
    `${week.malware} malware`,
    `${week.patterns} new patterns`,
    `${week.bad_hosts} bad script hosts`,
    `${week.safe} safe files and lines`,
    `${week.known_hosts} known script hosts (${week.known_hosts_auto} learned by themselves)`,
    `${week.false_alarms} false alarms removed`,
  ]
  return (
    <p style={{ margin: '0 0 8px' }}>
      <b>Learned in the last 7 days:</b> {parts.join(' · ')}. {week.to_review} waiting for review.
    </p>
  )
}
